<template>
  <Page
    :loading="isLoadingPage"
    :spinning="isSpinning"
    class="base-table"
  >
    <PageTitle>Send notifikationer</PageTitle>

    <div class="row">
      <div class="col-xs-12 col-lg-6 col-xl-7">
        <Box>
          <BaseInput
            label="Overskrift"
            v-model="form.title"
          />
          <BaseTextarea
            label="Besked"
            v-model="form.body"
          />

          <div class="row">
            <div class="col-xs-12">
              <BaseTable>
                <BaseTableGroup>
                  <BaseTableRow label="Notifikationstype">
                    <BaseRadioButton
                      v-for="type in types"
                      :key="type.id"
                      v-model="selectedType"
                      :store="type"
                      :label="type.name"
                      name="filterby"
                    />
                  </BaseTableRow>
                  <BaseTableRow label=" Side ved åbning af notifikation">
                    <div
                      v-for="target in targets"
                      :key="target.id"
                    >
                      <BaseRadioButton
                        v-model="selectedTarget"
                        :store="target.id"
                        :label="target.name"
                        name="filterby"
                      />
                      <component
                        :is="'Target' + target.id"
                        :selected="false"
                        :target="targetData"
                        :type="target.id"
                        v-show="target.id === selectedTarget"
                      />
                    </div>
                  </basetablerow>
                </BaseTableGroup>
              </BaseTable>
            </div>
          </div>

          <BaseButton
            @click="onPushNotification"
            label="Udsend notifikation"
            :disabled="$v.$invalid"
            v-if="!loadingPushNotification"
          />
          <BaseSpinner
            height="40px"
            width="40px"
            v-if="loadingPushNotification"
          />
        </Box>
      </div>
      <div class="col-xs-12 col-lg-6 col-xl-5">
        <Box>
          <table class="notifications">
            <tr
              v-for="notification in notifications"
              :key="notification.id"
              class="notification"
            >
              <td>
                <font-awesome-icon
                  :icon="['fab', notification.type === 'android' ? 'android' : 'apple']"
                  :class="'brand-' + notification.type"
                />
                <p class="notification__date">
                  {{ notification.send_time | HumanDate }}
                </p>
              </td>

              <td
                class="notification__name"
                :title="notification.name.length > 27 ? notification.name : ''"
              >
                {{ notification.name | maxlength }}
              </td>
              <td class="notification__state">
                {{ notification.state }}
              </td>
              <td class="notification__count">
                <span class="notification__success">{{ notification.pns_send_success }}</span>
              </td>
            </tr>
          </table>
        </Box>
        <p class="notificationUpdateInfo">
          Listen opdateres hvert {{ updateNotificationListInterval }}. sekund.
        </p>
      </div>
    </div>

    <div class="row">
      <div
        class="col-xs-6"
        v-if="isDebug"
      >
        <PageTitle class="whitelist">
          Whitelist
        </PageTitle>
        <p class="whitelistInfo">
          På testnmiljøet udsendes der kun notifikationer til den nedenstående liste af InstallationsID'er.<br>
          Indsæt InstallationID'er på de enheder, som du ønsker at teste udsendelse af notifkationer på.<br>
          InstallationsID'et skal også være registeret i test-databasens liste over installationss-ID'er. <br>
          Du finder installationsID'et nederst i Mere-menuen i app'en.
        </p>

        <Box>
          <div
            v-for="(item, itemIndex) in whitelist"
            :key="itemIndex"
            class="whitelistItems"
          >
            <BaseInput
              icon="times"
              v-model="item.installationID"
              @icon-click="onRemoveWhitelistItem(item.installationID)"
            />
          </div>

          <a
            href="#"
            class="addWhiteListItem highlight"
            @click.prevent="addWhitelistItem"
          >
            <font-awesome-icon icon="plus-circle" /> Tilføj
          </a>

          <BaseButton
            class="whitelistItems"
            label="Gem whitelist"
            :disabled="loadingSaveWhitelist"
            @click="onUpdateWhitelist"
          />
        </Box>
      </div>
    </div>
  </Page>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import BaseSpinner from '../../components/BaseSpinner.vue';
import BaseTableRow from '../../components/BaseTable/BaseTableRow.vue';
import BaseTableGroup from '../../components/BaseTable/BaseTableGroup.vue';
import BaseTable from '../../components/BaseTable/BaseTable.vue';
import BaseInput from '../../components/BaseInput.vue';
import BaseRadioButton from '../../components/BaseRadioButton.vue';
import BaseTextarea from '../../components/BaseTextarea.vue';
import Page from '../Page.vue';
import BaseButton from '../../components/BaseButton.vue';
import Box from '../../components/Box.vue';
import PageTitle from '../../components/PageTitle.vue';
import Pageload from '../../mixins/Pageload';
import NotificationService from '../../services/NotificationService';
import TargetMedicinSearch from './Targets/TargetMedicinSearch.vue';
import TargetMyMedicin from './Targets/TargetMyMedicin.vue';
import TargetPreparation from './Targets/TargetPreparation.vue';
import TargetSubstance from './Targets/TargetSubstance.vue';
import TargetUri from './Targets/TargetUri.vue';
import HumanDate from '../../filters/HumanDate';

export default {
    name: 'Notifications',
    mixins: [Pageload],
    components: {
        PageTitle,
        Box,
        BaseButton,
        Page,
        BaseTextarea,
        BaseRadioButton,
        BaseInput,
        TargetMedicinSearch,
        TargetMyMedicin,
        TargetPreparation,
        TargetSubstance,
        TargetUri,
        BaseTable,
        BaseTableGroup,
        BaseTableRow,
        BaseSpinner,
    },
    data() {
        return {
            types: [],
            targets: [],
            whitelist: [],
            notifications: [],
            selectedType: null,
            selectedTarget: null,
            form: {
                title: '',
                body: '',
            },
            loadingPushNotification: false,
            loadingSaveWhitelist: false,
            targetData: {},
            updateNotificationListInterval: 15,
            intervalReloadNotificationList: null,
        };
    },
    filters: {
        HumanDate,
        maxlength(value) {
            if (value.length > 27) {
                return `${value.slice(0, (27 - 3))}...`;
            }

            return value;
        },
    },
    validations: {
        form: {
            title: { required },
            body: { required },
        },
    },
    computed: {
        ...mapGetters({
            isAuthenticated: 'auth/isAuthenticated',
        }),
        formattedRequest() {
            let data = this.targetData[this.selectedTarget];

            if (typeof data === 'object') {
                if ('id' in data) {
                    data = data.id;
                }
            }

            return {
                title: this.form.title,
                body: this.form.body,
                action: {
                    target: this.selectedTarget,
                    data,
                },
            };
        },
        isDebug() {
            return process.env.VUE_APP_DEBUG.toString().includes('true');
        },
    },
    methods: {
        resetFields() {
            this.form = {
                title: '',
                body: '',
            };

            [this.selectedType] = this.types;
            this.selectedTarget = this.targets[0].id;
        },
        onRemoveWhitelistItem(installationID) {
            this.whitelist = this.whitelist.filter(item => item.installationID !== installationID);
        },
        addWhitelistItem() {
            this.whitelist.push({ installationID: '' });
        },
        async onPushNotification() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$snotify.error('Ikke alle felter er udfyldt korrekt.');
                return;
            }

            try {
                this.loadingPushNotification = true;
                await NotificationService.pushNotification(this.selectedType.id, this.formattedRequest);
                this.$snotify.success(`${this.form.title} er sendt ud`);
                this.resetFields();
                this.loadingPushNotification = false;
                this.loadNotificationList();
            } catch (error) {
                this.loadingPushNotification = false;
            }
        },
        async onUpdateWhitelist() {
            try {
                this.loadingSaveWhitelist = true;
                await NotificationService.saveWhitelist(this.whitelist);
                this.$snotify.success('Whitelist er gemt');
                this.loadingSaveWhitelist = false;
            } catch (error) {
                this.loadingSaveWhitelist = false;
            }
        },
        async loadNotificationList() {
            if (!this.isAuthenticated) {
                return;
            }
            const mapData = (item, type) => ({ ...item, type });

            try {
                const list = await NotificationService.list();

                let mappedAndSort = [...list.android.map(item => mapData(item, 'android')), ...list.ios.map(item => mapData(item, 'ios'))];
                mappedAndSort = mappedAndSort.sort((a, b) => {
                    if (window.moment(a.send_time) > window.moment(b.send_time)) {
                        return -1;
                    }
                    if (window.moment(a.send_time) < window.moment(b.send_time)) {
                        return 1;
                    }
                    return 0;
                });

                this.notifications = mappedAndSort;
            } catch (error) {
                //
            }
        },
    },
    async created() {
        try {
            this.loadNotificationList();
            this.types = await NotificationService.types();
            this.targets = await NotificationService.targets();

            this.whitelist = await NotificationService.whitelist();
            // this.whitelist = temp.map(item => ({ a: item.installationID, ole: item.installationID }));

            this.resetFields();

            this.targets.forEach((type) => {
                this.$set(this.targetData, type, '');
            });
            this.pageloaded();


            this.intervalReloadNotificationList = setInterval(this.loadNotificationList, (this.updateNotificationListInterval * 1000));
        } catch (error) {
            //
        }
    },
    destroyed() {
        clearInterval(this.intervalReloadNotificationList);
    },
};
</script>

<style lang="scss" scoped>
.whitelist {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 50px;
    margin-bottom: 20px;

    /deep/ .base-button {
        font-size: 13px;
    }
}

.addWhiteListItem {
    display: block;
    padding-top: 10px;
    text-align: center;
    font-weight: bold;
}

.whitelistItems {
    &:not(:first-child) {
        margin-top: 20px;
    }
}

.whitelistInfo {
    margin: 20px 0;
    line-height: 1.9;
}

.brand-ios {
    color: #000;
}
.brand-android {
    color: #78C257;
}

.notifications {
    display: flex;
    list-style: none;
    flex-direction: column;
}

.notificationUpdateInfo {
    margin-top: -10px;
    text-align: right;
    font-size: 11px;
    font-style: italic;
}

.notification {
    padding: 10px;
    display: flex;
    justify-content: space-between;
    font-size: 12px;

    &:nth-child(odd) {
        background-color: $gray2;
    }
    p {
        display: inline-block;
    }

    &__success {
        color: green;
        font-weight: bold;
    }
    &__count {
        width: 65px;
        // margin-left: 15px;
        padding-left: 1px;
        text-align: right;
    }
    &__error {
        color: red;
    }
    &__date {
        margin-left: 5px;
    }
    &__name {
        flex-grow: 1;
        margin-left: 10px;
        // font-weight: bold;
    }
}
</style>
